import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';

/**
 * AdminLayout Component
 *
 * Layout wrapper for Super Admin dashboard pages.
 * Provides consistent sidebar navigation, header, and content area.
 *
 * @example
 * <Route path="/admin" element={<RequireSuperAdmin><AdminLayout /></RequireSuperAdmin>}>
 *   <Route index element={<AdminDashboard />} />
 *   <Route path="users" element={<AdminUsers />} />
 * </Route>
 */

interface NavItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
}

const navItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: ICONS.DASHBOARD, end: true },
  { path: '/admin/users', label: 'Users', icon: ICONS.USERS },
  { path: '/admin/teams', label: 'Teams', icon: ICONS.TEAM },
  { path: '/admin/agencies', label: 'Brand Portfolios', icon: 'group' },
  { path: '/admin/packages', label: 'Packages', icon: 'cube' },
  { path: '/admin/affiliates', label: 'Affiliates', icon: 'bond-hand' },
  { path: '/admin/notifications', label: 'Notifications', icon: ICONS.BELL },
  { path: '/admin/branding', label: 'Branding', icon: ICONS.IMAGE },
  { path: '/admin/help', label: 'Help Videos', icon: ICONS.PLAY },
  { path: '/admin/status', label: 'Status & Feedback', icon: ICONS.PULSE },
  { path: '/admin/events', label: 'Events', icon: ICONS.ANALYTICS },
  { path: '/admin/system', label: 'System', icon: ICONS.SETTINGS },
];

export const AdminLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const asideRef = useRef<HTMLElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const firstNavLinkRef = useRef<HTMLAnchorElement | null>(null);
  const wasOpenRef = useRef(false);

  // Off-screen on mobile when the drawer is closed - must not be reachable
  // by keyboard/screen-reader navigation while visually hidden.
  const drawerClosedOnMobile = !isDesktop && !sidebarOpen;

  const handleExit = () => {
    navigate('/');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Focus management for the mobile drawer: move focus in on open, restore
  // focus to the hamburger button on close, and support Escape-to-close.
  useEffect(() => {
    if (isDesktop) return;

    if (sidebarOpen) {
      wasOpenRef.current = true;

      if (firstNavLinkRef.current) {
        firstNavLinkRef.current.focus();
      } else {
        asideRef.current?.focus();
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          setSidebarOpen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    // Only restore focus to the hamburger button when the drawer was
    // previously open - avoid stealing focus on initial mount.
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      hamburgerRef.current?.focus();
    }
  }, [sidebarOpen, isDesktop]);

  // Resolve the active nav item by longest-prefix match of the current path
  const currentPageLabel = (() => {
    let bestMatch: NavItem | null = null;
    for (const item of navItems) {
      const isMatch = item.end
        ? location.pathname === item.path
        : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
      if (isMatch && (!bestMatch || item.path.length > bestMatch.path.length)) {
        bestMatch = item;
      }
    }
    return bestMatch?.label || 'Dashboard';
  })();

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.fullName) {
      return user.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'A';
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex">
      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        ref={asideRef}
        // Closed drawer on mobile is only visually hidden via transform, so it
        // must be pulled out of the tab order + a11y tree while off-screen.
        aria-hidden={drawerClosedOnMobile || undefined}
        tabIndex={-1}
        className={`w-64 bg-white fixed h-screen flex flex-col shadow-sm border-r border-gray-200 z-40 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo/Brand */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-1">
            <img
              src="/images/logo.png"
              alt="Ägile Ässets"
              className="h-8 w-auto"
            />
            <h1 className="text-xl font-bold text-[#ff5757]">
              Admin
            </h1>
          </div>
          <p className="text-xs text-gray-600 font-medium">Ägile Ässets Panel</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" role="navigation" aria-label="Admin navigation">
          {navItems.map((item, index) => (
            <NavLink
              key={item.path}
              ref={index === 0 ? firstNavLinkRef : undefined}
              to={item.path}
              end={item.end}
              tabIndex={drawerClosedOnMobile ? -1 : undefined}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-full transition-all duration-200 ${
                  isActive
                    ? 'bg-[#ff5757] text-white shadow-sm'
                    : 'text-gray-700 hover:bg-red-50 hover:text-[#ff5757]'
                }`
              }
            >
              <i className={`hm hm-${item.icon} text-lg`} aria-hidden="true" />
              <span className="font-medium text-sm">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff5757] to-[#ff7a7a] flex items-center justify-center text-white font-semibold text-sm shadow-sm">
              {getUserInitials()}
            </div>
            <div className="flex-1 min-w-0">
              <Tooltip content={user?.fullName || user?.email || ''} disabled={!user?.fullName && !user?.email}>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user?.fullName || 'Admin'}
                </div>
              </Tooltip>
              <Tooltip content={user?.email || ''} disabled={!user?.email}>
                <div className="text-xs text-gray-600 truncate">
                  {user?.email}
                </div>
              </Tooltip>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="w-full text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 font-medium"
            tabIndex={drawerClosedOnMobile ? -1 : undefined}
            onClick={handleExit}
          >
            <i className={`hm hm-${ICONS.ARROW_LEFT} mr-2`} aria-hidden="true" />
            Exit Admin
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 sticky top-0 z-10 shadow-sm">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 min-w-0">
              <button
                ref={hamburgerRef}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors lg:hidden shrink-0"
                aria-label="Open navigation"
                onClick={() => setSidebarOpen(true)}
              >
                <i className={`hm hm-${ICONS.HAMBURGER} text-gray-700 text-lg`} aria-hidden="true" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900 truncate">{currentPageLabel}</h2>
              <span className="px-3 py-1 rounded-full text-xs font-semibold text-white bg-[#ff5757] shadow-sm shrink-0">
                Super Admin
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Notification bell */}
              <button
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Notifications"
                onClick={() => navigate('/admin/notifications')}
              >
                <i className="hm hm-bell text-gray-700 text-lg" aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          <div className="max-w-7xl mx-auto overflow-x-hidden">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};
